import React from 'react';
import { Circle } from 'lucide-react';
import { cn } from '../lib/utils';

export interface TimelineItem {
  timestamp: string;
  title: string;
  description?: string;
  icon?: React.ReactNode;
  variant?: 'default' | 'success' | 'warning' | 'error' | 'info';
}

export interface TimelineProps {
  items: TimelineItem[];
  className?: string;
}

const variantClasses: Record<string, { dot: string; iconBg: string }> = {
  default: {
    dot: 'border-slate-300 bg-white',
    iconBg: 'bg-slate-100 text-slate-600',
  },
  success: {
    dot: 'border-emerald-400 bg-emerald-50',
    iconBg: 'bg-emerald-100 text-emerald-600',
  },
  warning: {
    dot: 'border-amber-400 bg-amber-50',
    iconBg: 'bg-amber-100 text-amber-600',
  },
  error: {
    dot: 'border-red-400 bg-red-50',
    iconBg: 'bg-red-100 text-red-600',
  },
  info: {
    dot: 'border-blue-400 bg-blue-50',
    iconBg: 'bg-blue-100 text-blue-600',
  },
};

export function Timeline({ items, className }: TimelineProps) {
  if (items.length === 0) return null;

  return (
    <div className={cn('relative', className)}>
      {items.map((item, idx) => {
        const isLast = idx === items.length - 1;
        const variant = item.variant ?? 'default';
        const classes = variantClasses[variant] ?? variantClasses['default']!;

        return (
          <div key={idx} className="relative flex gap-4 pb-8 last:pb-0">
            {/* Connecting line */}
            {!isLast && (
              <div
                className="absolute left-[15px] top-8 h-[calc(100%-20px)] w-px bg-slate-200"
                aria-hidden="true"
              />
            )}

            {/* Icon / dot */}
            <div className="relative shrink-0">
              {item.icon ? (
                <div
                  className={cn(
                    'flex h-8 w-8 items-center justify-center rounded-full [&_svg]:h-4 [&_svg]:w-4',
                    classes.iconBg,
                  )}
                >
                  {item.icon}
                </div>
              ) : (
                <div
                  className={cn(
                    'flex h-8 w-8 items-center justify-center rounded-full border-2',
                    classes.dot,
                  )}
                >
                  <Circle className="h-2 w-2 fill-current text-current opacity-60" />
                </div>
              )}
            </div>

            {/* Content */}
            <div className="flex-1 pt-1">
              <div className="flex items-start justify-between gap-4">
                <h4 className="font-body text-body-sm font-medium text-text-heading">
                  {item.title}
                </h4>
                <time className="shrink-0 whitespace-nowrap font-body text-caption text-text-subtle">
                  {item.timestamp}
                </time>
              </div>
              {item.description && (
                <p className="mt-1 font-body text-body-sm leading-relaxed text-text-muted">
                  {item.description}
                </p>
              )}
            </div>
          </div>
        );
      })}
    </div>
  );
}
